import React, { useRef, useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { API_URL, getUploadUrl } from '../config/api';
import './css/mainviewcatsslider.css';

const MainViewCatsSlider = () => {
    const { t, language } = useTranslation();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const sliderRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [showTip, setShowTip] = useState(true);
    const [hasInteracted, setHasInteracted] = useState(false);
    const [hasMoved, setHasMoved] = useState(false);

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/categories`);
            // Filter only active categories and sort by display_order
            const activeCategories = response.data
                .filter(cat => cat.is_active)
                .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
            setCategories(activeCategories);
        } catch (error) {
            console.error('Error fetching categories:', error);
        } finally {
            setLoading(false);
        }
    };

    // Transform categories for display with translations
    const cats = useMemo(() => {
        return categories.map(cat => ({
            id: cat.id,
            name: language === 'ar' ? cat.name_ar : cat.name_en,
            key: cat.key_name,
            image: cat.image ? getUploadUrl(cat.image) : null,
            link: `/${cat.key_name}`,
            width: '180px' // Default width, can be adjusted
        }));
    }, [categories, language]);

    // Initialize scroll position to right (RTL)
    useEffect(() => {
        if (sliderRef.current) {
            const slider = sliderRef.current;
            slider.scrollLeft = 0;
        }
    }, [cats]);

    const handleMouseDown = (e) => {
        if (!hasInteracted) {
            setHasInteracted(true);
            setShowTip(false);
        }
        setIsDragging(true);
        setHasMoved(false);
        setStartX(e.pageX - sliderRef.current.offsetLeft);
        setScrollLeft(sliderRef.current.scrollLeft);
        sliderRef.current.style.cursor = 'grabbing';
        sliderRef.current.style.userSelect = 'none';
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
        if (sliderRef.current) {
            sliderRef.current.style.cursor = 'grab';
            sliderRef.current.style.userSelect = 'auto';
        }
    };

    const handleMouseUp = (e) => {
        if (hasMoved && e) {
            e.preventDefault();
        }
        setIsDragging(false);
        setHasMoved(false);
        if (sliderRef.current) {
            sliderRef.current.style.cursor = 'grab';
            sliderRef.current.style.userSelect = 'auto';
        }
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - sliderRef.current.offsetLeft;
        const walk = (x - startX) * 2;
        
        if (Math.abs(walk) > 5) {
            setHasMoved(true);
        }
        
        if (!hasInteracted) {
            setHasInteracted(true);
            setShowTip(false);
        }
        sliderRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleTouchStart = (e) => {
        if (!hasInteracted) {
            setHasInteracted(true);
            setShowTip(false);
        }
        setIsDragging(true);
        setHasMoved(false);
        setStartX(e.touches[0].pageX - sliderRef.current.offsetLeft);
        setScrollLeft(sliderRef.current.scrollLeft);
    };

    const handleTouchMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.touches[0].pageX - sliderRef.current.offsetLeft;
        const walk = (x - startX) * 2;
        
        if (Math.abs(walk) > 5) {
            setHasMoved(true);
        }
        
        sliderRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleTouchEnd = (e) => {
        if (hasMoved && e) {
            e.preventDefault();
        }
        setIsDragging(false);
        setHasMoved(false);
    };

    if (loading) {
        return (
            <div className='main-view-cats-slider'>
                <div className='container-fluid'>
                    <div className='loading-message'>
                        <p>{t('categories.loading') || 'Loading...'}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='main-view-cats-slider'>
            <div className='container-fluid'>
                <div className='main-view-cats-slider-wrapper'>
                    <div 
                        className="slider"
                        ref={sliderRef}
                        onMouseDown={handleMouseDown}
                        onMouseLeave={handleMouseLeave}
                        onMouseUp={handleMouseUp}
                        onMouseMove={handleMouseMove}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        {cats.map((cat) => (
                            <div 
                                className='slide' 
                                key={cat.id}
                                style={{ width: cat.width }}
                            >
                                <Link 
                                    to={cat.link} 
                                    onClick={(e) => {
                                        if (hasMoved || isDragging) {
                                            e.preventDefault();
                                        }
                                    }}
                                    className="slider-link"
                                >
                                    <div className='cats-slider-item'>
                                        {cat.image ? (
                                            <img src={cat.image} alt={cat.name} className='cat-image' />
                                        ) : (
                                            <div className='cat-image-placeholder'>{cat.name}</div>
                                        )}
                                        <h3>{cat.name}</h3>
                                    </div>
                                </Link>
                            </div>
                        ))}
                    </div>
                    {showTip && cats.length > 0 && (
                        <div className="slider-tip-overlay">
                            <div className="tip-content">
                                <svg 
                                    className="grab-icon" 
                                    width="24" 
                                    height="24" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path 
                                        d="M9 5C9 6.10457 8.10457 7 7 7C5.89543 7 5 6.10457 5 5C5 3.89543 5.89543 3 7 3C8.10457 3 9 3.89543 9 5Z" 
                                        stroke="currentColor" 
                                        strokeWidth="2" 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round"
                                    />
                                    <path 
                                        d="M9 12C9 13.1046 8.10457 14 7 14C5.89543 14 5 13.1046 5 12C5 10.8954 5.89543 10 7 10C8.10457 10 9 10.8954 9 12Z" 
                                        stroke="currentColor" 
                                        strokeWidth="2" 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round"
                                    />
                                    <path 
                                        d="M9 19C9 20.1046 8.10457 21 7 21C5.89543 21 5 20.1046 5 19C5 17.8954 5.89543 17 7 17C8.10457 17 9 17.8954 9 19Z" 
                                        stroke="currentColor" 
                                        strokeWidth="2" 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round"
                                    />
                                    <path 
                                        d="M19 5C19 6.10457 18.1046 7 17 7C15.8954 7 15 6.10457 15 5C15 3.89543 15.8954 3 17 3C18.1046 3 19 3.89543 19 5Z" 
                                        stroke="currentColor" 
                                        strokeWidth="2" 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round"
                                    />
                                    <path 
                                        d="M19 12C19 13.1046 18.1046 14 17 14C15.8954 14 15 13.1046 15 12C15 10.8954 15.8954 10 17 10C18.1046 10 19 10.8954 19 12Z" 
                                        stroke="currentColor" 
                                        strokeWidth="2" 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round"
                                    />
                                    <path 
                                        d="M19 19C19 20.1046 18.1046 21 17 21C15.8954 21 15 20.1046 15 19C15 17.8954 15.8954 17 17 17C18.1046 17 19 17.8954 19 19Z" 
                                        stroke="currentColor" 
                                        strokeWidth="2" 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <span className="tip-text">{t('nav.dragTip')}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MainViewCatsSlider;
